import {
  ContractInfo,
  GetHotTokensParams,
  HotTokensSortKeys,
  PoolInfo,
} from 'api/types/httpsTypes/contracts';
import { formatNumberWithSuffix, safeBig } from 'utils/FormatNumber';
import { AddToFavouritesButton } from '../../../components';
import { EthAddress } from 'modules/ethereum/components/EthAddress';
import { TokenIconWithChain } from 'modules/shared-components/asset/token-icon';
import { ContractAlerts } from '../../../components/ContractAlerts';
import { useUserState } from 'modules/user/UserContext';
import { ReactComponent as TwitterIcon } from 'assets/img/brands/twitter.svg';
import dexScreenerImg from 'assets/img/brands/dexscreener.svg';
import { StatsCard, StatsCardTitle, StatsCardValue } from '../StatsCard';
import {
  chainIsOnWebacy,
  Chains,
  chainScan,
  chainScanImg,
} from 'api/types/httpsTypes/d-wallets';
import { useQuery } from '@tanstack/react-query';
import {
  ContractService,
  contractServiceKeys,
  transFormPeriodInMinutes,
} from 'api/services/httpServices/ContractService';
import { HotTokensPeriods } from '../Wallets/usePageParams';
import WebacyIcon from '../../../../../assets/img/brands/webacy.jpg';
import Button from 'modules/shared-components/button/button';
import { ChartBarSquareIcon, ClockIcon } from '@heroicons/react/24/outline';
import { WhitelistButton } from '../SnipingOpportunitiesCard/columns';
import { useToggle } from 'modules/utils/useToggle';
import { useTokenStats } from 'api/hooks/useTokenStats';
import { PerformanceStyle } from '../../../components/alerts/performanceStyle';
import { useMemo } from 'react';
import { useWebsocket } from 'api/hooks/useWebsocket';
import { useTokenPrice } from 'api/hooks/useTokenPrice';
import { DurationCounter } from '../TelegramCalls/columns';

export function TokenPoolInfoHeader({
  contract,
  selectedPool,
}: {
  contract: ContractInfo;
  selectedPool: PoolInfo | undefined;
}) {
  const params: GetHotTokensParams = {
    sort: HotTokensSortKeys.TotalBuyTop,
    address: contract.generalInfo.address,
    chain: contract.generalInfo.chain.key,
    period: HotTokensPeriods.TwentyFourHour,
  };

  const tokenPriceSub = useMemo(
    () => ({
      chain: contract.generalInfo.chain.key,
      address: contract.generalInfo.address,
    }),
    [contract.generalInfo.chain.key, contract.generalInfo.address]
  );

  const statsSub = useMemo(
    () => ({
      chain: contract.generalInfo.chain.key,
      address: contract.generalInfo.address,
      timeFrame: transFormPeriodInMinutes(HotTokensPeriods.TwentyFourHour),
    }),
    [contract.generalInfo.chain.key, contract.generalInfo.address]
  );

  useWebsocket('token_price', tokenPriceSub);
  useWebsocket('token_stats', statsSub);

  const tokenPrice = useTokenPrice(
    contract.generalInfo.chain.key,
    contract.generalInfo.address
  );

  const tokenStats = useTokenStats(
    contract.generalInfo.chain.key,
    contract.generalInfo.address,
    HotTokensPeriods.TwentyFourHour
  );

  const { data: poolsInfoFromBe } = useQuery({
    queryKey: contractServiceKeys.getHotTokens(params),
    queryFn: () => ContractService.getHotTokens(params),
    keepPreviousData: true,
  });

  const price = tokenPrice
    ? safeBig(tokenPrice)
    : safeBig(selectedPool?.priceDollar);

  const previousPrice = safeBig(tokenStats?.sp);
  const tokenPriceChange24h =
    !price.eq(0) && !previousPrice.eq(0)
      ? safeBig(price).minus(previousPrice).div(previousPrice)
      : null;

  const change24hPct = tokenPriceChange24h
    ? safeBig(tokenPriceChange24h).mul(100)
    : safeBig(poolsInfoFromBe?.[0]?.priceChange).mul(100);

  const volume24h = tokenStats?.v
    ? safeBig(tokenStats.v)
    : safeBig(poolsInfoFromBe?.[0]?.volume);
  const liquidity = tokenStats?.l
    ? safeBig(tokenStats.l)
    : safeBig(selectedPool?.poolLiquidity);
  const marketCap = tokenStats?.m
    ? safeBig(tokenStats.m)
    : safeBig(contract.generalInfo.totalMarketCap);
  const liquidityMarketCapRatio =
    liquidity.gt(0) && marketCap.gt(0)
      ? liquidity.div(marketCap).mul(100).toFixed(1)
      : 0;

  return (
    <div className="flex justify-between items-center">
      <>
        <div className="flex xxs:gap-2 items-center xxs:mt-1">
          {price && (
            <div className=" xxs:hidden lg:block flex xxs:space-x-1 xxs:items-end">
              <PerformanceStyle
                className="xxs:text-lg xxs:font-semibold"
                formatFn={formatNumberWithSuffix}
                pnl={price.toString()}
                isOnlyFlashEnabled={true}
              />
              {!change24hPct.eq(0) ? (
                <PerformanceStyle
                  className="xxs:text-xs xxs:pb-1"
                  asset="%"
                  pnl={change24hPct.toString()}
                />
              ) : (
                <div>--</div>
              )}
            </div>
          )}
          {volume24h && (
            <div className="xxs:hidden lg:block flex xxs:space-x-1  items-center">
              <div className="xxs:text-xs xxs:text-dex-white-secondary xxs:pt-1.5">
                24h vol:
              </div>
              <PerformanceStyle
                className="xxs:text-lg xxs:font-semibold"
                pnl={volume24h.toString()}
                noPnl={true}
              />
            </div>
          )}
          {liquidity && (
            <div className="xxs:hidden lg:block flex xxs:space-x-1 items-center">
              <div className="xxs:text-xs xxs:text-dex-white-secondary xxs:pt-1.5">
                Liq:
              </div>
              <PerformanceStyle
                className="xxs:text-lg xxs:font-semibold"
                pnl={liquidity.toString()}
                noPnl={true}
              />
            </div>
          )}
          {marketCap && (
            <div className="xxs:hidden lg:block flex xxs:space-x-1 items-center">
              <div className="xxs:text-xs xxs:text-dex-white-secondary xxs:pt-1.5">
                M.Cap:
              </div>
              <PerformanceStyle
                className="xxs:text-lg xxs:font-semibold"
                pnl={marketCap.toString()}
                noPnl={true}
              />
            </div>
          )}

          {liquidityMarketCapRatio && (
            <div className="xxs:hidden lg:block flex xxs:space-x-1 items-center">
              <div className="xxs:text-xs xxs:text-dex-white-secondary xxs:pt-1.5">
                Liq/M. Cap:
              </div>
              <PerformanceStyle
                className="xxs:text-lg xxs:font-semibold"
                pnl={liquidityMarketCapRatio.toString()}
                asset="%"
                noPnl={true}
              />
            </div>
          )}
        </div>
        <div className="xxs:grid grid-cols-2 lg:hidden xxs:gap-1 items-center">
          {price && (
            <StatsCard className="">
              <StatsCardValue>
                <div className="xxs:text-lg xxs:leading-6 xxs:hidden lg:block xxs:flex xxs:space-x-1 xxs:items-center">
                  <PerformanceStyle
                    formatFn={formatNumberWithSuffix}
                    pnl={price.toString()}
                    isOnlyFlashEnabled={true}
                  />
                  <PerformanceStyle
                    className="xxs:text-xs xxs:pt-1.5"
                    asset="%"
                    pnl={change24hPct.toString()}
                  />
                </div>
              </StatsCardValue>
              <StatsCardTitle>Price</StatsCardTitle>
            </StatsCard>
          )}
          {volume24h && (
            <StatsCard className="">
              <StatsCardValue className="xxs:text-lg xxs:font-semibold">
                $
                {formatNumberWithSuffix(volume24h, {
                  precision: 0,
                })}
              </StatsCardValue>
              <StatsCardTitle>24h vol</StatsCardTitle>
            </StatsCard>
          )}
          {liquidity && (
            <StatsCard className="">
              <StatsCardValue className="xxs:text-lg xxs:font-semibold">
                $
                {formatNumberWithSuffix(liquidity, {
                  precision: 0,
                })}
              </StatsCardValue>
              <StatsCardTitle>Liq</StatsCardTitle>
            </StatsCard>
          )}
          {marketCap && (
            <StatsCard className="">
              <StatsCardValue className="xxs:text-lg xxs:font-semibold">
                {formatNumberWithSuffix(marketCap, {
                  precision: 0,
                })}
              </StatsCardValue>
              <StatsCardTitle>M.Cap</StatsCardTitle>
            </StatsCard>
          )}
        </div>
      </>
      {contract.generalInfo.creationDatetime && (
        <div className="flex text-xs space-x-1 xxs:pr-1 items-center xxs:text-dex-white-secondary">
          <ClockIcon className="w-3 h-3" />
          <div> Live since:</div>
          <div className="">
            <DurationCounter
              start={contract.generalInfo.creationDatetime.toString()}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export function TokenNameHeader({
  contract,
  selectedPool,
}: {
  contract: ContractInfo;
  selectedPool: PoolInfo | undefined;
}) {
  const { user } = useUserState();
  const [openModal, { open, close }] = useToggle();

  return (
    <span className="xxs:flex xxs:gap-2 items-center">
      <div className="relative">
        <TokenIconWithChain
          hasIcon={contract.generalInfo.hasIcon}
          chain={contract.generalInfo.chain.key}
          protocol={selectedPool?.dex?.key}
          tokenName={contract.generalInfo.name ?? '?'}
          className="h-[25px] w-[25px]"
          address={contract.generalInfo.address}
          symbol={contract.generalInfo.symbol || undefined}
        />
      </div>
      <div className="truncate">
        {' '}
        <span className=" whitespace-nowrap max-w-md xxs:text-3xl xxs:font-semibold">
          {contract.generalInfo.name}{' '}
        </span>
        <span className="whitespace-nowrap xxs:w-[120px] max-w-xs md:w-auto  xxs:text-xl xxs:text-dex-white-secondary xxs:font-semibold">
          {contract.generalInfo.symbol}
        </span>
      </div>
      {user && (
        <AddToFavouritesButton
          chain={contract.generalInfo.chain.key}
          contractAddress={contract.generalInfo.address}
          className="xxs:w-8 xxs:h-8 flex-shrink-0"
        />
      )}
      {user && (
        <Button
          type="button"
          onClick={open}
          className="xxs:p-1"
          variant="dexNeutral"
        >
          <ChartBarSquareIcon className="xxs:w-5 xxs:h-5" />
        </Button>
      )}
      {openModal && (
        <WhitelistButton
          close={close}
          symbol={contract.generalInfo.symbol ?? ''}
          tokenAddress={contract.generalInfo.address}
        />
      )}
      <div className="xxs:hidden md:block">
        <ContractAlerts contract={contract} />
      </div>
    </span>
  );
}

export function TokenInfoHeader({
  contract,
  selectedPool,
}: {
  contract: ContractInfo;
  selectedPool: PoolInfo | undefined;
}) {
  return (
    <div className="flex xxs:gap-2 items-center">
      {contract.generalInfo.address && (
        <div className="xxs:text-sm xxs:text-dex-white-secondary xxs:font-normal flex items-center xxs:gap-1">
          Token:{' '}
          <EthAddress
            chain={contract.generalInfo.chain.key}
            address={contract.generalInfo.address}
            kind="token"
          />
        </div>
      )}
      {selectedPool?.poolAddress && (
        <div className="xxs:text-sm xxs:text-dex-white-secondary xxs:font-normal flex items-center xxs:gap-1">
          Pair:{' '}
          <EthAddress
            chain={contract.generalInfo.chain.key}
            address={selectedPool.poolAddress}
          />
        </div>
      )}
      <ExternalInfo
        chain={contract.generalInfo.chain.key}
        contract={contract}
        selectedPool={selectedPool}
      />
    </div>
  );
}

export function ExternalInfo({
  selectedPool,
  contract,
  chain,
}: {
  contract: ContractInfo | undefined;
  selectedPool: PoolInfo | undefined;
  chain: Chains;
}) {
  return (
    <div className="flex xxs:gap-1 text-[1rem] leading-none">
      {selectedPool && (
        <a
          rel="noopener noreferrer"
          target="_blank"
          href={`https://dexscreener.com/${
            chain === Chains.Ethereum
              ? 'ethereum'
              : chain === Chains.Solana
              ? 'solana'
              : chain
          }/${selectedPool.poolAddress}`}
        >
          <img
            src={dexScreenerImg}
            alt="dexscreener logo"
            className="w-4 h-4"
          />
        </a>
      )}

      {selectedPool && (
        <a
          rel="noopener noreferrer"
          target="_blank"
          href={`https://${chainScan(chain)}/address/${
            selectedPool?.poolAddress
          }`}
        >
          <img src={chainScanImg(chain)} alt="scan img" className="w-4 h-4" />
        </a>
      )}

      {contract && (
        <a
          rel="noopener noreferrer"
          className="hidden lg:block"
          target="_blank"
          href={`https://twitter.com/search?q=$${contract.generalInfo.address}`}
        >
          <TwitterIcon className="w-4 h-4 xxs:bg-black dark:bg-white rounded-full p-[3px] dark:fill-black fill-white" />
        </a>
      )}

      {contract && chainIsOnWebacy(contract?.generalInfo.chain.key) && (
        <a
          rel="noopener noreferrer"
          target="_blank"
          href={`https://dapp.webacy.com/dyor/address/${contract?.generalInfo.address}?chain=${contract?.generalInfo.chain.key}`}
        >
          <img src={WebacyIcon} className="w-4 h-4" />
        </a>
      )}
    </div>
  );
}
